import {
  FilterClauseType,
  filterExistsAndExcludes,
  getTypedKeys,
  removeEmptyFilters,
  useProductTypesFilter,
  useSymbolsFilter,
  type BlotterTableFilter,
  type FilterClause,
  type FilterableProperty,
  type Security,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isEqual, pick } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import type { PriceBlotterEntity } from './PriceBlotterEntity';
import type { PriceBlotterFilter } from './types';

export interface UseMultilegPriceParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export function usePriceBlotterFilter({ persistedBlotterTable }: UseMultilegPriceParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const [initialFilter] = useState(persistedBlotterTable.initialState?.filter);
  const [filter, setFilter] = useState<PriceBlotterFilter>((initialFilter as PriceBlotterFilter) || {});

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;
      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const symbolsFilter = useSymbolsFilter();
  const productTypesFilter = useProductTypesFilter();

  const clientSideFilter = useCallback(
    (item: PriceBlotterEntity) => {
      if (filterExistsAndExcludes(filter, 'Symbols', { Symbol: item.security.Symbol }, 'Symbol')) {
        return false;
      }

      if (filterExistsAndExcludes(filter, 'ProductTypes', { ProductType: item.security.ProductType }, 'ProductType')) {
        return false;
      }

      return true;
    },
    [filter]
  );

  const filterableProperties: FilterableProperty<keyof PriceBlotterFilter>[] = useMemo(
    () => compact([symbolsFilter, { ...productTypesFilter, key: 'ProductTypes' }]),
    [symbolsFilter, productTypesFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      getTypedKeys(filter).forEach(key => {
        if (Array.isArray(filter[key])) {
          clauses.push({
            key: key,
            type: FilterClauseType.INCLUSIVE,
            selections: filter[key],
          });
        }
      });
    }
    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: PriceBlotterFilter = removeEmptyFilters<PriceBlotterFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PriceBlotterFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;

  return {
    initialFilter,
    filter,
    clientSideFilter,
    changeFilter,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Symbol: 'Symbols',
  ProductType: 'ProductTypes',
} satisfies Partial<Record<keyof Security, keyof PriceBlotterFilter>>;
export function colIDToFilterBuilderKey(id: string): keyof PriceBlotterFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof PriceBlotterFilter
    | undefined;
}

export function onlyServerFilterKeys(filter?: PriceBlotterFilter) {
  return filter == null ? filter : pick(filter, []);
}
